import { extendTheme } from '@chakra-ui/react';

import BORDER_STYLERS from './border-styles';
import BORDER_WIDTHS from './border-width';
import BREAKPOINTS from './breakpoints';
import COLORS from './colors';
import BUTTON from './components/button.theme';
import MENU from './components/menu.theme';
import CHECKBOX from './components/checkbox.theme';
import RADIO from './components/radio.theme';
import SLIDER from './components/slider.theme';
import TABLE from './components/table.theme';
import CARD from './components/card.theme';
import TABS from './components/tabs.theme';
import TOOLTIP from './components/tooltip.theme';
import TAG from './components/tag.theme';
import TEXT from './components/text.theme';
import LIST from './components/list.theme';
import DIVIDER from './components/divider.theme';
import FONT_SIZES from './font-sizes';
import FONTS from './fonts';
import GLOBAL from './global';
import LINE_HEIGHT from './line-height';
import RADII from './radii';
import SIZES from './sizes';
import SPACES from './spaces';

export const BLOCK_OVERVIEW_COLORS = {
  primary: '#8B5CF6',
  textSecondary: '#708090',
  textDark: '#121826',
  border: '#D9D9D9',
  borderSubtle: '#E8E9EB',
  danger: '#FE552F',
  dangerHover: '#E54B29',
  activeBg: '#F5F0FF',
  placeholder: '#BEBEBE',
  info: '#3BA7FA',
  white: '#FFFFFF',
} as const;

const theme = extendTheme({
  borderWidths: BORDER_WIDTHS,
  borderStyles: BORDER_STYLERS,
  breakpoints: BREAKPOINTS,
  fonts: FONTS,
  fontSizes: FONT_SIZES,
  lineHeights: LINE_HEIGHT,
  colors: COLORS,
  radii: RADII,
  space: SPACES, // margins & paddings
  sizes: SIZES, // widths & heights
  styles: { global: GLOBAL },
  components: {
    Button: BUTTON,
    Menu: MENU,
    Checkbox: CHECKBOX,
    Text: TEXT,
    Card: CARD,
    Radio: RADIO,
    Slider: SLIDER,
    Tabs: TABS,
    Tag: TAG,
    Table: TABLE,
    List: LIST,
    Tooltip: TOOLTIP,
    Divider: DIVIDER,
  },
  /**
   * Chakra semantic tokens – these are the new design-system level colors.
   * They are purely additive, so all your existing color usage keeps working.
   */
  semanticTokens: {
    colors: {
      // Brand
      'brand.primary': { default: '#8B5CF6' },
      'brand.primaryStrong': { default: 'violet.900' },
      'brand.primarySoft': { default: 'grey.500' },
      'brand.secondary': { default: 'green.900' },
      'brand.secondarySoft': { default: 'green.500' },
      'brand.accent': { default: 'accent.900' },

      // Surfaces
      'surface.background': { default: 'black.900' },
      'surface.backgroundAlt': { default: 'violet.900' },
      'surface.card': { default: 'white.900' },
      'surface.border': { default: 'grey.700' },

      // Text
      'text.primary': { default: 'white.900' },
      'text.primaryDark': { default: 'black.900' }, // Primary text on light surfaces (black)
      'text.secondary': { default: '#708090' },
      'text.muted': { default: 'grey.900' },
      'text.inverse': { default: 'black.900' },

      // border colors
      'border.primary': { default: '#D9D9D9' },

      // Status / feedback
      'status.success': { default: 'green.800' },
      'status.successSoft': { default: 'green.500' },
      'status.error': { default: 'red.500' },
      'status.errorStrong': { default: 'red.900' },
      'status.info': { default: 'cyan.500' },
      'status.warning': { default: 'pink.900' },

      // Block overview semantic tokens
      'blockOverview.primary': { default: BLOCK_OVERVIEW_COLORS.primary },
      'blockOverview.textDark': { default: BLOCK_OVERVIEW_COLORS.textDark },
      'blockOverview.textSecondary': { default: BLOCK_OVERVIEW_COLORS.textSecondary },
      'blockOverview.border': { default: BLOCK_OVERVIEW_COLORS.border },
      'blockOverview.borderSubtle': { default: BLOCK_OVERVIEW_COLORS.borderSubtle },
      'blockOverview.danger': { default: BLOCK_OVERVIEW_COLORS.danger },
      'blockOverview.dangerHover': { default: BLOCK_OVERVIEW_COLORS.dangerHover },
      'blockOverview.activeBg': { default: BLOCK_OVERVIEW_COLORS.activeBg },
      'blockOverview.placeholder': { default: BLOCK_OVERVIEW_COLORS.placeholder },
      'blockOverview.info': { default: BLOCK_OVERVIEW_COLORS.info },
      'blockOverview.white': { default: BLOCK_OVERVIEW_COLORS.white },
    },
  },
});

export default theme;
